/**
 * Larguras dos glifos da Helvetica e da Helvetica-Bold, em milésimos do corpo — a unidade dos
 * arquivos AFM da Adobe. Servem para quebrar linha na medida certa antes de escrever o PDF.
 *
 * As duas são fontes "base 14": todo leitor de PDF já as tem, então nada precisa ser embutido
 * no arquivo. É isso que mantém o PDF na casa dos poucos KB e o texto selecionável.
 *
 * Só o ASCII imprimível está tabelado. Letras acentuadas caem em `baseGlyph()` e medem como a
 * letra sem acento, o que é exato na Helvetica para os acentos do português — a exceção são as
 * variantes de `i`, que são mais largas que o `i` simples e estão tratadas à parte.
 */

// prettier-ignore
const HELVETICA: readonly number[] = [
  // 32..47
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  // 48..63  (dígitos têm todos a mesma largura)
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  // 64..79
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
  // 80..95
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  // 96..111
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
  // 112..126
  556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];

// prettier-ignore
const HELVETICA_BOLD: readonly number[] = [
  // 32..47
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
  // 48..63
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
  // 64..79
  975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
  // 80..95
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
  // 96..111
  333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
  // 112..126
  611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584,
];

const FIRST_CODE = 32;
const LAST_CODE = 126;

/** Largura usada quando o caractere não é reconhecido — a do espaço, deliberadamente estreita. */
const FALLBACK_WIDTH = 278;

/**
 * Letra sem acento equivalente, para reaproveitar a largura tabelada. As variantes de `i`
 * ficam de fora de propósito: `í`/`ì`/`î`/`ï` medem 278 na Helvetica, e não os 222 do `i`.
 */
const I_ACENTUADO = /[íìîïÍÌÎÏ]/;

function baseGlyph(char: string): string {
  return char.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Largura de um caractere, em milésimos do corpo. */
function charWidth(char: string, bold: boolean): number {
  if (I_ACENTUADO.test(char)) {
    return 278;
  }

  const semAcento = baseGlyph(char);
  const code = semAcento.charCodeAt(0);

  if (Number.isNaN(code) || code < FIRST_CODE || code > LAST_CODE) {
    return FALLBACK_WIDTH;
  }

  const tabela = bold ? HELVETICA_BOLD : HELVETICA;
  return tabela[code - FIRST_CODE] ?? FALLBACK_WIDTH;
}

/** Largura de um texto em pontos, para um dado corpo. */
export function measureText(texto: string, size: number, bold: boolean): number {
  let milesimos = 0;
  for (const char of texto) {
    milesimos += charWidth(char, bold);
  }
  return (milesimos * size) / 1000;
}
